import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { GuardianInvite } from '@se-po/shared-data-access-models'
import { HouseholdGuardianInvitesService, UserPersonaService } from '@se-po/shared-data-access-services'
import { HouseholdInviteComponent } from '@se-po/shared-feature-household-invite'
import { AddChildProfileComponent } from '@se-po/shared-ui-add-child-profile'
import { Observable, zip } from 'rxjs'
import { SeFeModalComponent } from 'se-fe-modal'
import { SeFeToastService } from 'se-fe-toast'
import {
  CentralServiceResources as cs,
  UserServiceResources as us
} from 'se-resource-types'

@Component({
  selector: 'se-po-add-profile-modal',
  templateUrl: './add-profile-modal.component.html',
  styleUrls: ['./add-profile-modal.component.scss'],
})
export class AddProfileModalComponent {
  @Input() public selfPersona: cs.Profiles.Persona
  @Input() public userId: number
  @Output() public addedProfile: EventEmitter<cs.Profiles.Persona> = new EventEmitter()
  @Output() public modalClosed: EventEmitter<void> = new EventEmitter()
  @ViewChild('childInfo') public childInfo: AddChildProfileComponent
  @ViewChild('householdInvite') public householdInvite: HouseholdInviteComponent
  @ViewChild('modal') public modal: SeFeModalComponent

  public showModal = true // hack until we can fix the modal
  public addChildStep = 1
  public addPersona: us.Persona
  public guardianInvites: GuardianInvite[] = []
  public loading = false
  public error: string

  constructor(
    private userPersonaService: UserPersonaService,
    private householdGuardianInvitesService: HouseholdGuardianInvitesService,
    private seFeToastService: SeFeToastService,
  ) {
  }

  public open(evt): void {
    this.addPersona = null
    this.error = ''
    this.addChildStep = 1
    this.childInfo.reset()
    this.guardianInvites = []
    this.loading = false
    this.modal.open(evt)
  }

  public onAfterClose(): void {
    this.showModal = false
    setTimeout(() => this.showModal = true)
  }

  public onInvite(event: GuardianInvite): void {
    this.error = ''
    const isExistingEmail = this.selfPersona.email_addresses.find(e => e.address === event?.email) ||
      this.guardianInvites.find(i => i.email === event?.email)
    if (isExistingEmail) {
      this.error = 'A guardianship or invitation already exists for the given email address.'
      return
    }
    if (event) {
      this.guardianInvites.push(event)
    }
    this.addChildStep = 3
  }

  public onCancelInvite(email: string): void {
    this.guardianInvites = this.guardianInvites.filter(guardianInvite => guardianInvite.email !== email)
  }

  public onAddAnother(): void {
    this.error = ''
    this.householdInvite.reset()
    this.addChildStep = 2
  }

  public onAddProfileClose(): void {
    this.modal.close()
    this.modalClosed.emit()
  }

  public onContinue(): void {
    if (!this.guardianInvites.length) {
      this.onAddProfileClose()
      return
    }
    zip(...this.guardianInvites.map(invite => this.submitOne(invite, this.addPersona.id)))
      .subscribe((guardianships: cs.Households.PersonaGuardianInvite[]) => {
          const successGuardianships = guardianships.filter(guardianship => !!guardianship)
          if (successGuardianships.length === guardianships.length) {
            guardianships.forEach(guardianship =>
              this.seFeToastService.success('Success: parent/guardian has been added!'))
            this.onAddProfileClose()
          }
          else {
            this.error = 'Guardians can\'t be completed, please try again.'
            this.seFeToastService.error(this.error)
          }
      })
  }

  public submitOne(invite: GuardianInvite, id: number): Observable<cs.Households.PersonaGuardianInvite> {
      return this.householdGuardianInvitesService.create(
        invite.access,
        invite.email,
        id,
        'persona-picker'
      )
    }

    public addProfile(): void {
      this.loading = true
      if (!this.childInfo.isValid()) {
        this.loading = false
        return
      }

      this.userPersonaService
        .create(this.userId, this.childInfo.value)
        .subscribe({
          next: result => {
            this.loading = false
            this.addPersona = result
            this.addChildStep = 2
            this.addedProfile.emit(result)
          },
          error: error => {
            this.loading = false
            this.error = error?.error?.error?.messages?.[0] || 'Could not create child, please try again'
            this.seFeToastService.error(this.error)
          }
        })

    }
}
